<template>
  <div class="announcement">
    <div class="announcement__container">
      <div class="announcement__content">
        <template v-if="!loading">
          <p-breadcrumb
            :items="[
              { label: $t('general.home'), to: '/' },
              { label: $t('general.announcements'), to: '#' },
            ]"
          />

          <p-content-title :title="$t('general.announcements')" :social-icons="[]" />

          <div class="announcement-items-control mb-16">
            <p-segmented-control
              id="cat"
              :selected-index="selectedCategory"
              :items="categories"
              :prefer-query-navigation="true"
              size="lg"
            />
          </div>

          <template v-if="announcementList && announcementList.items.length > 0">
            <p-announcement
              v-for="(announcement, announcementIndex) in announcementList.items"
              :key="`announcement--${announcementIndex}`"
              :icon="announcement.icon_url"
              :category="announcement.category_title"
              :category-link="appLink(['announcement', announcement.category_slug])"
              :date="announcement.published_at"
              :reading-time="announcement.read_duration_minutes"
              :heading="announcement.title"
              :content="announcement.summary"
              :link="
                appLink(['announcement', `${announcement.category_slug}/${announcement.slug}`])
              "
              :divider="announcementIndex < announcementList.items.length - 1"
            />
            <p-pagination :max-page-number="announcementList.total_pages || 1" />
          </template>
          <template v-else>
            <div class="f-body-16 t-fg-secondary" data-test="announcement-empty">
              {{ $t('announcement.emptyForCategory') }}
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useAnnouncementList, useAnnouncementCategories } from '@/api/useAnnouncement'
import { useAppLink } from '@/composables/useAppLink'

const { appLink } = useAppLink()

const route = useRoute()

const { announcementList, fetchAnnouncementList, loading } = useAnnouncementList()
const { announcementCategories } = useAnnouncementCategories()

const selectedCategory = ref(0)
const categories = ref<Array<{ key: string; display: string; url: string }>>([])
const currentCategoryKey = computed(() => route.query.cat?.toString() || '')

const turkishToEnglish = (str: string) => {
  return str.normalize('NFD').replace(/[\u0300-\u036F]/g, '')
}

const getKeyByName = (name: string): string => {
  return turkishToEnglish(name).toLowerCase().replaceAll(/\s/g, '-')
}

categories.value = [
  {
    display: $t('general.all'),
    key: '',
    url: `/${route.params.page?.toString() || 'duyuru'}`,
  },
]

watch(
  () => announcementCategories.value,
  (list) => {
    if (Array.isArray(list)) {
      const page = route.params.page?.toString() || 'duyuru'
      categories.value = [
        {
          display: $t('general.all'),
          key: '',
          url: `/${page}`,
        },
        ...list.map((cat) => ({
          key: getKeyByName(cat.title),
          display: cat.title,
          url: `/${page}/${getKeyByName(cat.title)}`,
        })),
      ]

      const matchedIndex = list.findIndex(
        (cat) => getKeyByName(cat.title) === currentCategoryKey.value,
      )
      selectedCategory.value = matchedIndex >= 0 ? matchedIndex + 1 : 0

      if (matchedIndex >= 0) {
        fetchAnnouncementList(list[matchedIndex].id)
      } else {
        fetchAnnouncementList('')
      }
    }
  },
  { immediate: true },
)

watch(
  () => currentCategoryKey.value,
  (key) => {
    const list = announcementCategories.value || []
    if (Array.isArray(list)) {
      const matchedIndex = list.findIndex((cat) => getKeyByName(cat.title) === key)
      selectedCategory.value = matchedIndex >= 0 ? matchedIndex + 1 : 0
      if (matchedIndex >= 0) {
        fetchAnnouncementList(list[matchedIndex].id)
      } else {
        fetchAnnouncementList('')
      }
    }
  },
)

definePageMeta({
  layout: 'default',
})
</script>
